'use client'

// imports
import { useAtom } from 'jotai'
import { useCallback } from 'react';

// lib files
import { searchFiltersAtom } from '@/lib/atoms/search-filters';
import {
  ACCESSIBILITY_AVAILABILITY,
  ALL_POSSIBLE_VALUES,
  PASSPORT_STATUS,
  RESULT_TYPES,
  VIDEO_LENGTHS,
} from '@/lib/types/search/searchFilters';
import { DefaultSearchFiltersObject, SearchFiltersObject } from '@/lib/content-services/search/search';

// svg's
import CloseIcon from '@/public/svg/pbs-close-white.svg';

// styles
import styles from './SearchFilters.module.scss';
import { objectsMatchShallow } from '@/lib/helpers/equality';
import { searchResultsAtom } from '@/lib/atoms/search-results';

interface SearchFiltersProps {
  className?: string;
  station: {
    id?: string;
    commonName?: string;
  };
}

function SearchFilters(props: SearchFiltersProps) {
  const { className, station } = props;
  const [searchFilters, setSearchFilters] = useAtom(searchFiltersAtom)
  const stationCommonName = station?.commonName || '';
  const [searchResults, _] = useAtom(searchResultsAtom)

  const areResultsFiltered = !objectsMatchShallow({ ...searchFilters, query: '' }, DefaultSearchFiltersObject)

  let filterableShows = null

  if(searchResults?.resultsCounts?.shows) {
    filterableShows = structuredClone(searchResults?.resultsCounts?.shows)
    filterableShows.unshift({ count: null, value: 'All' })
  }

  const updateFilter = useCallback((key: keyof SearchFiltersObject, value: ALL_POSSIBLE_VALUES) => {
    setSearchFilters(previousFilters => ({ ...previousFilters, [key]: value }))
  }, [setSearchFilters])

  const resetFilters = useCallback(() => (
    setSearchFilters({ ...DefaultSearchFiltersObject, query: searchFilters.query })
  ), [searchFilters, setSearchFilters])

  const getCountForFilterValue = useCallback((value: string) => searchResults?.resultsCounts[value], [searchResults])

  let classes = `${styles.search_filters} `

  if (className) {
    classes += className
  }

  return (
    <div className={classes}>
      <h3>Filter By:</h3>
      <form action='#' onSubmit={(e) => e.preventDefault()}>
        <fieldset className={styles.search_filters__item}>
          <legend>Type</legend>
          {RESULT_TYPES.map((resultType, i) => {
            const isAllOption = resultType.value === 'all';
            const filterCount = getCountForFilterValue(resultType.value);
            return (
              !!filterCount || isAllOption ? <span className={styles.radio} key={i}>
                <input
                  type='radio'
                  name='result-type'
                  value={resultType.value}
                  aria-checked={searchFilters.resultType === resultType.value}
                  checked={searchFilters.resultType === resultType.value}
                  onChange={() => updateFilter('resultType', resultType.value)}
                />
                <label
                  htmlFor={resultType.value}
                  onClick={() => updateFilter('resultType', resultType.value)}
                >
                  {resultType.label}
                  {!isAllOption && ` (${filterCount})`}
                </label>
              </span>
              : null
          )})}
        </fieldset>

        {filterableShows && (
          <fieldset className={styles.search_filters__item}>
            <legend>Shows</legend>
            {filterableShows.map((show, i) => {
              const filterCount = show.count
              let checked = false

              if(searchFilters?.filterShow === show.value) {
                checked = true
              } else if(!searchFilters.filterShow && show.value === 'All') {
                checked = true
              }

              return (
                <span className={styles.radio} key={i}>
                  <input
                    type='radio'
                    name='show'
                    value={show.value}
                    aria-checked={checked}
                    checked={checked}
                    // The as ALL_POSSIBLE_VALUES is a result of two things.
                    // We want as much type safety as possible around these filters
                    // BUT we also can't define a type for every possible PBS show.
                    // This allows us to force the filter update, while still
                    // achieving the typed structure checking.
                    onChange={() => updateFilter('filterShow', show.value as ALL_POSSIBLE_VALUES)}
                  />
                  <label
                    htmlFor={show.value}
                    onClick={() => updateFilter('filterShow', show.value as ALL_POSSIBLE_VALUES)}
                  >
                    {show.value}
                    {filterCount && ` (${filterCount})`}
                  </label>
                </span>
            )})}
          </fieldset>
        )}

        <fieldset className={styles.search_filters__item}>
          <legend>Video Length</legend>
          {VIDEO_LENGTHS.map((videoLength, i) => {
            const isAllOption = videoLength.value === 'all';
            const filterCount = getCountForFilterValue(videoLength.value);
            return (
              !!filterCount || isAllOption ? <span className={styles.radio} key={i}>
                <input
                  type='radio'
                  name='video-length'
                  value={videoLength.value}
                  aria-checked={searchFilters.duration === videoLength.value}
                  checked={searchFilters.duration === videoLength.value}
                  onChange={() => updateFilter('duration', videoLength.value)}
                />
                <label
                  htmlFor={videoLength.value}
                  onClick={() => updateFilter('duration', videoLength.value)}
                >
                  {videoLength.label}
                  {!isAllOption && ` (${filterCount})`}
                </label>
              </span>
              : null
          )})}
        </fieldset>

        <fieldset className={styles.search_filters__item}>
          <legend>{stationCommonName} Passport</legend>
          {PASSPORT_STATUS.map((statusOption, i) => {
            const isAllOption = statusOption.value === 'all';
            const filterCount = getCountForFilterValue(statusOption.value);
            return (
              !!filterCount || isAllOption ? <span className={styles.radio} key={i}>
                <input
                  type='radio'
                  name='passport-status'
                  value={statusOption.value}
                  aria-checked={searchFilters.passportStatus === statusOption.value}
                  checked={searchFilters.passportStatus === statusOption.value}
                  onChange={() => updateFilter('passportStatus', statusOption.value)}
                />
                <label
                  htmlFor={statusOption.value}
                  onClick={() => updateFilter('passportStatus', statusOption.value)}
                >
                  {statusOption.label === 'Passport' ? `${stationCommonName} Passport`: statusOption.label}
                  {!isAllOption && ` (${filterCount})`}
                </label>
              </span>
              : null
          )})}
        </fieldset>

        <fieldset className={styles.search_filters__item}>
          <legend>Accessibility Features</legend>
          {ACCESSIBILITY_AVAILABILITY.map((accessibilityOption, i) => {
            const isAllOption = accessibilityOption.value === 'all';
            const filterCount = !isAllOption ? getCountForFilterValue('descriptive') : null;
            return (
              !!filterCount || isAllOption ? <span className={styles.radio} key={i}>
                <input
                  type='radio'
                  name='accessibility-feature'
                  value={accessibilityOption.value}
                  aria-checked={searchFilters.accessibility === accessibilityOption.value}
                  checked={searchFilters.accessibility === accessibilityOption.value}
                  onChange={() => updateFilter('accessibility', accessibilityOption.value)}
                />
                <label
                  htmlFor={accessibilityOption.value}
                  onClick={() => updateFilter('accessibility', accessibilityOption.value)}
                >
                  {accessibilityOption.label}
                  {!isAllOption && ` (${filterCount})`}
                </label>
              </span>
              : null
          )})}
        </fieldset>

        {areResultsFiltered && (
          <button
            className={styles.search_filters__reset_button}
            onClick={resetFilters}
          >
            Reset All Filters
            <CloseIcon />
          </button>
        )}
      </form>
    </div>
  )
}

export default SearchFilters
